
import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  ArrowUp,
  BrainCircuit,
  Check,
  ExternalLink,
  Gamepad2,
  Layers3,
  Mail,
  Network,
  Sparkles,
  WalletCards,
  Zap
} from "lucide-react";
import "./Styles.css";

/* ======================================================
   FOOTER DATA
   ====================================================== */

const footerNavigation = [
  {
    heading: "PLATFORM",
    links: [
      { label: "Proprietary Games", to: "/originals-custom" },
      { label: "Multi-Tenant Infrastructure", to: "/info/multi-tenant/infrastructure" },
      { label: "Provably Fair Engine", to: "/provably-fair-engine" },
      { label: "Risk Management", to: "/risk-management" }
    ]
  },
  {
    heading: "FOR OPERATORS",
    links: [
      { label: "Explore Pricing", to: "/pricing" },
      { label: "Book a Demo", to: "/book-demo" },
      { label: "Request Documentation", to: "/request-documentation" },
      { label: "Frequently Asked Questions", to: "/general/frequently-asked-questions" }
    ]
  },
  {
    heading: "COMPANY",
    links: [
      { label: "About Ethosino", to: "/about" },
      { label: "Company Overview", to: "/company" },
      { label: "Architecture Walkthrough", to: "/company/architecture-walkthrough" },
      { label: "Careers", to: "/company/careers" }
    ]
  }
];

const systemModules = [
  {
    id: "games",
    eyebrow: "CONTENT LAYER",
    title: "35+ Games",
    icon: Gamepad2,
    position: "games"
  },
  {
    id: "tenants",
    eyebrow: "DEPLOYMENT",
    title: "Multi-Tenant",
    icon: Network,
    position: "tenants"
  },
  {
    id: "wallets",
    eyebrow: "FINANCIAL",
    title: "Wallet + Ledger",
    icon: WalletCards,
    position: "wallets"
  },
  {
    id: "ai",
    eyebrow: "INTELLIGENCE",
    title: "Operator AI",
    icon: BrainCircuit,
    position: "ai"
  }
];

const marqueeItems = [
  "FIRST-PARTY GAMES",
  "TENANT PROVISIONING",
  "CONNECTED WALLETS",
  "OPERATOR ADMIN",
  "FAIRNESS EVIDENCE",
  "RISK CONTROLS",
  "AI-ASSISTED INSIGHTS"
];

/* ======================================================
   FOOTER COMPONENT
   ====================================================== */

const EthosinoFooter = () => {
  const reduceMotion = useReducedMotion();
  const ctaRef = useRef(null);

  const handlePointerMove = event => {
    if (reduceMotion || !ctaRef.current) return;

    const rect = ctaRef.current.getBoundingClientRect();

    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    ctaRef.current.style.setProperty("--ethfx-x", `${x}%`);
    ctaRef.current.style.setProperty("--ethfx-y", `${y}%`);
  };

  const revealProps = {
    initial: reduceMotion ? false : { opacity: 0, y: 35 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: {
      duration: 0.75,
      ease: [0.16, 1, 0.3, 1]
    }
  };

  return (
    <footer className="eth-footer-next" id="ethosino-footer">

      {/* AMBIENT BACKGROUND */}
      <div className="eth-footer-next__ambient" aria-hidden="true">
        <div className="eth-footer-next__grid" />
        <div className="eth-footer-next__aura eth-footer-next__aura--one" />
        <div className="eth-footer-next__aura eth-footer-next__aura--two" />
      </div>

      <div className="eth-footer-next__shell">

        {/* ===========================================
            THE FINAL CTA
        ============================================ */}

        <motion.section
          {...revealProps}
          className="eth-footer-next__cta"
          ref={ctaRef}
          onPointerMove={handlePointerMove}
          aria-labelledby="eth-footer-next-title"
        >
          <div className="eth-footer-next__cta-glow" aria-hidden="true" />

          <div className="eth-footer-next__cta-content">

            <div className="eth-footer-next__eyebrow">
              <span className="eth-footer-next__eyebrow-line" />
              BUILT FOR CASINO OPERATORS
              <Sparkles size={14} />
            </div>

            <h2 id="eth-footer-next-title">
              Your brand
              <span> out front.</span>
              <br />
              The operating system
              <em> behind it.</em>
            </h2>

            <p>
              Proprietary games, automated tenant provisioning,
              wallets, operator controls, and AI-assisted
              intelligence — built into one connected platform.
            </p>

            <div className="eth-footer-next__cta-actions">
              <a
                href="https://demo.ethosino.com"
                target="_blank"
                rel="noopener noreferrer"
                className="eth-footer-next__button eth-footer-next__button--primary"
              >
                EXPLORE LIVE PLATFORM
                <ArrowUpRight size={19} />
              </a>

              <Link
                to="/book-demo"
                className="eth-footer-next__button eth-footer-next__button--secondary"
              >
                BOOK AN OPERATOR DEMO
                <ArrowRight size={18} />
              </Link>
            </div>

            <div className="eth-footer-next__cta-evidence">
              <div>
                <Check size={14} />
                First-party game catalog
              </div>
              <div>
                <Check size={14} />
                Connected operating tools
              </div>
              <div>
                <Check size={14} />
                Branded tenant environments
              </div>
            </div>

          </div>

          {/* =======================================
              IMMERSIVE OPERATING SYSTEM VISUAL
          ======================================== */}

          <div className="eth-footer-next__system" aria-label="Connected Ethosino platform layers">

            <div className="eth-footer-next__system-caption">
              <span>ETHOSINO SYSTEM ARCHITECTURE</span>
              <span>ONE CONNECTED CORE</span>
            </div>

            <div className="eth-footer-next__orbit-stage">
              <div className="eth-footer-next__orbit eth-footer-next__orbit--outer" aria-hidden="true" />
              <div className="eth-footer-next__orbit eth-footer-next__orbit--middle" aria-hidden="true" />
              <div className="eth-footer-next__orbit eth-footer-next__orbit--inner" aria-hidden="true" />

              <div className="eth-footer-next__orbit-beacon eth-footer-next__orbit-beacon--one" aria-hidden="true" />
              <div className="eth-footer-next__orbit-beacon eth-footer-next__orbit-beacon--two" aria-hidden="true" />

              {/* CENTER */}

              <motion.div
                className="eth-footer-next__core"
                animate={
                  reduceMotion
                    ? undefined
                    : { y: [0, -6, 0] }
                }
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <div className="eth-footer-next__core-icon">
                  <Layers3 size={29} />
                </div>

                <span>THE OPERATING LAYER</span>
                <strong>ETHOSINO</strong>
                <small>ONE CONNECTED PLATFORM</small>
                <div className="eth-footer-next__core-divider" />

                <div className="eth-footer-next__core-bottom">
                  <i />
                  TENANT-AWARE
                </div>
              </motion.div>

              {/* SURROUNDING MODULES */}

              {systemModules.map((module, index) => {
                const Icon = module.icon;

                return (
                  <motion.div
                    key={module.id}
                    className={`eth-footer-next__module eth-footer-next__module--${module.position}`}
                    initial={reduceMotion ? false : { opacity: 0, scale: 0.88 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: 0.25 + index * 0.12,
                      duration: 0.55
                    }}
                  >
                    <div className="eth-footer-next__module-icon">
                      <Icon size={18} />
                    </div>

                    <div>
                      <span>{module.eyebrow}</span>
                      <strong>{module.title}</strong>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <div className="eth-footer-next__system-foot">
              <span>
                <span className="eth-footer-next__pulse-dot" />
                CONNECTED PLATFORM COMPONENTS
              </span>

              <span>GAMES / MONEY / CONTROL / AI</span>
            </div>

          </div>
        </motion.section>

        {/* ===========================================
            ANIMATED CAPABILITY RIBBON
        ============================================ */}

        <div
          className="eth-footer-next__ribbon"
          aria-label="Ethosino platform capabilities"
        >
          <div
            className={`eth-footer-next__ribbon-track ${reduceMotion ? "is-static" : ""
              }`}
          >
            {[0, 1].map(copy => (
              <div
                className="eth-footer-next__ribbon-group"
                key={copy}
                aria-hidden={copy === 1}
              >
                {marqueeItems.map(item => (
                  <span key={item}>
                    <i />
                    {item}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* ===========================================
            MAIN FOOTER NAVIGATION
        ============================================ */}

        <div className="eth-footer-next__navigation">

          {/* BRAND */}

          <div className="eth-footer-next__brand">
            <Link
              to="/"
              className="eth-footer-next__brand-logo"
              aria-label="Ethosino homepage"
            >
              <span className="eth-footer-next__brand-symbol">
                <Layers3 size={23} />
              </span>
              ETHOSINO
              <span className="eth-footer-next__brand-period">.</span>
            </Link>

            <p>
              Turnkey casino software for operators building
              differentiated brands — powered by proprietary
              games and a connected operating system.
            </p>

            <a
              href="mailto:founder@ethosino.com"
              className="eth-footer-next__email"
            >
              <Mail size={17} />
              founder@ethosino.com
              <ArrowUpRight size={14} />
            </a>

            <div className="eth-footer-next__social-row">
              <a
                href="https://www.instagram.com/ethosino_gaming"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Ethosino on Instagram"
                className="eth-footer-next__social"
              >
                <img
                  src={require("../../../assets/icons/instagram.png")}
                  alt=""
                  width="19"
                  height="19"
                  className="eth-footer-next__social-image"
                />
              </a>

              <a
                href="https://demo.ethosino.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Explore Ethosino live demo"
                className="eth-footer-next__social"
              >
                <ExternalLink size={18} />
              </a>

              <Link
                to="/book-demo"
                aria-label="Contact Ethosino"
                className="eth-footer-next__social"
              >
                <Mail size={18} />
              </Link>
            </div>
          </div>

          {/* LINK COLUMNS */}

          {footerNavigation.map(column => (
            <nav
              className="eth-footer-next__nav-column"
              key={column.heading}
              aria-label={column.heading}
            >
              <h3>{column.heading}</h3>

              <ul>
                {column.links.map(item => (
                  <li key={item.to}>
                    <Link to={item.to}>
                      <span>{item.label}</span>
                      <ArrowUpRight size={14} />
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}

          {/* OPERATOR CONTACT */}

          <div className="eth-footer-next__contact-col">
            <span className="eth-footer-next__contact-kicker">
              DIRECT OPERATOR ACCESS
            </span>

            <div className="eth-footer-next__contact-icon">
              <Zap size={21} />
            </div>

            <h3>Building a casino brand?</h3>

            <p>
              Discuss your requirements, integrations,
              deployment model, and pilot opportunities.
            </p>

            <Link to="/book-demo">
              LET'S TALK
              <ArrowUpRight size={16} />
            </Link>
          </div>

        </div>

        {/* ===========================================
            OVERSIZED SIGNATURE
        ============================================ */}

        <div className="eth-footer-next__signature" aria-hidden="true">
          ETHOSINO
        </div>

        {/* ===========================================
            BOTTOM BAR
        ============================================ */}

        <div className="eth-footer-next__bottom">
          <div className="eth-footer-next__copyright">
            © {new Date().getFullYear()} Ethosino.
            <span> All rights reserved.</span>
          </div>

          <div className="eth-footer-next__bottom-identity">
            <span />
            BUILT FOR OPERATORS. ENGINEERED FOR CONTROL.
          </div>

          <button
            type="button"
            className="eth-footer-next__back-top"
            onClick={() =>
              window.scrollTo({
                top: 0,
                behavior: reduceMotion ? "auto" : "smooth"
              })
            }
          >
            BACK TO TOP
            <ArrowUp size={16} />
          </button>
        </div>

        <p className="eth-footer-next__disclaimer">
          Ethosino provides software infrastructure, not legal
          authorization to operate. Operators are responsible
          for applicable licensing, regulatory compliance,
          jurisdictional restrictions, and required integrations.
        </p>

      </div>
    </footer>
  );
};

export default EthosinoFooter;
